import React, { ReactElement } from 'react'
import {
  Box,
  ScreenReaderOnly,
  TextHeading,
  Icon,
  Link as MuiLink,
} from '@ally/metronome-ui'
import { MuiIconMap, muiInProgress, muiErrorFill } from '@ally/metronome-icons'
import { DefaultTheme } from 'styled-components'
import {
  StatusTrackerActivity,
  DebitCardOrderEvent,
} from '@ally/shared-status-tracker'
import { List, ListItem } from '../SharedStyles'
import { STATUS_TRACKER_ACTIVITY } from './constants'
import StatusTrackerTipsModal from './StatusTrackerTipsModal'
import {
  GetDescriptionOpts,
  AchEvent,
  ActivityTypes,
  StatusTypes,
  WireStatuses,
  AchStatuses,
  CheckOrderStatuses,
  StatusOpts,
  WireEvent,
  DebitCardOrderStatuses,
} from './types'
import { isAfterDeliverOnDate } from '../utils'
import { useHostServices } from '../../../HostServices'

interface StatusTrackerTipsProps {
  activities: StatusTrackerActivity[]
}

interface ListItemData {
  description: (date?: string) => string | ReactElement
  icon: MuiIconMap
  iconColor?: keyof DefaultTheme['colors']
}

const getListItemData = ({
  type,
  status,
  deliverOnDate,
  contactUsFlag,
  isWiresMigrationEnabled,
  isDebitCardStatusTrackerEnabled,
  isFunded,
}: GetDescriptionOpts): ListItemData => {
  const activityType = STATUS_TRACKER_ACTIVITY[type]
  const afterDeliverOnDate = isAfterDeliverOnDate(deliverOnDate)
  let activityStatus: StatusOpts = {
    description: () => '',
    icon: muiInProgress,
  }

  if (type === ActivityTypes.WireTransfer) {
    if (isWiresMigrationEnabled && contactUsFlag) {
      activityStatus = {
        description: (): React.ReactElement => {
          return (
            <>
              Action required: Call us at{' '}
              <MuiLink
                text="1-877-247-2559"
                href="tel:18772472559"
                aria-label="1, 8 7 7, 2 4 7, 2 5 5 9"
              />
              . We need more info to complete your wire transfer.
            </>
          )
        },
        icon: muiErrorFill,
        iconColor: 'warning',
      }
    } else {
      activityStatus = (activityType as Record<WireStatuses, StatusOpts>)[
        status as WireStatuses
      ]
    }
  }
  if (type === ActivityTypes.ACHTransfer) {
    activityStatus = (activityType as Record<AchStatuses, StatusOpts>)[
      afterDeliverOnDate ? 'Completed' : (status as AchStatuses)
    ]
  }
  if (type === ActivityTypes.CheckOrder) {
    activityStatus = (activityType as Record<CheckOrderStatuses, StatusOpts>)[
      status as CheckOrderStatuses
    ]
  }

  if (
    isDebitCardStatusTrackerEnabled &&
    type === ActivityTypes.DebitCardOrder
  ) {
    if (status === DebitCardOrderStatuses.Ordered && !isFunded) {
      activityStatus = {
        description: (): string =>
          "Add money to your account, and we'll ship your card.",
        icon: muiErrorFill,
        iconColor: 'warning',
      }
    } else {
      activityStatus = (activityType as Record<
        DebitCardOrderStatuses,
        StatusOpts
      >)[status as DebitCardOrderStatuses]
    }
  }

  return {
    description: activityStatus.description,
    icon: activityStatus.icon,
    iconColor: activityStatus.iconColor as keyof DefaultTheme['colors'],
  }
}

const StatusTrackerTips: React.FC<StatusTrackerTipsProps> = ({
  activities,
}) => {
  const { featureFlags } = useHostServices()
  const isWiresMigrationEnabled = featureFlags.variation(
    'FF_wires-migration-status-tracker',
    false,
  )
  const isDebitCardStatusTrackerEnabled = featureFlags.variation(
    'FF_debit-card-status-tracker',
    false,
  )
  return (
    <>
      <ScreenReaderOnly>
        <TextHeading tag="h2" size="md">
          Status Tracker
        </TextHeading>
      </ScreenReaderOnly>
      <List data-testid="st-dashboard-tips">
        {activities.map(activity => {
          const type = activity.activityName as ActivityTypes
          const { deliverOnDate } = activity.eventModelData as AchEvent
          const status = activity.statuses[0].status as StatusTypes
          const canceledDate =
            activity.statuses[0].status === 'Canceled'
              ? activity.statuses[0].eventDateTime
              : null
          const { contactUsFlag } = activity.eventModelData as WireEvent
          const {
            isFunded,
            estimatedDeliveryDate,
          } = activity.eventModelData as DebitCardOrderEvent
          const { description, icon, iconColor } = getListItemData({
            type,
            status,
            deliverOnDate,
            contactUsFlag,
            isWiresMigrationEnabled,
            isDebitCardStatusTrackerEnabled,
            isFunded,
          })

          return (
            <ListItem
              key={activity.transactionId}
              data-testid="status-tracker-item"
            >
              <Icon
                icon={icon}
                unsupportedCustomWidth={18}
                unsupportedCustomHeight={18}
                fill={iconColor || 'plum'}
                ariaHidden
                mt="1px"
                mr="xs"
                data-testid={icon.base.title}
              />
              <Box flex="1">
                <Box display="inline" mr="xs">
                  {description(
                    type === 'DebitCardOrder'
                      ? estimatedDeliveryDate
                      : (canceledDate as string),
                  )}
                </Box>
                <StatusTrackerTipsModal activity={activity} />
              </Box>
            </ListItem>
          )
        })}
      </List>
    </>
  )
}

export default StatusTrackerTips
